import {createMultiStyleConfigHelpers} from '@chakra-ui/styled-system';

import typography from '@amblique/king-living/app/theme/foundations/typography';

const helpers = createMultiStyleConfigHelpers(['root', 'stack', 'header', 'heading', 'chevronWrapper', 'chevron', 'editLink', 'content']);

/**
 * ToggleCard Component Theme
 *
 * Multi-part component with the following parts:
 * - root: The outer container of the toggle card
 * - header: The clickable header section containing heading and chevron
 * - heading: The title/heading text
 * - chevron: The chevron icon (up/down)
 * - content: The content area that expands/collapses
 */
export const ToggleCard = helpers.defineMultiStyleConfig({
  baseStyle: {
    root: {
      position: 'relative',
      bg: 'white',
      border: '1px solid',
      borderColor: 'gray.100',
      borderRadius: 'base',
      px: {base: 4, md: 6},
      py: 6,
      shadow: 'base',
    },
    stack: {
      spacing: 4,
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      cursor: 'pointer',
      transition: 'opacity 0.2s',
      _hover: {
        opacity: 0.8,
      },
      _disabled: {
        cursor: 'default',
      },
    },
    heading: {
      fontSize: 'lg',
      fontWeight: 'semibold',
      lineHeight: 'tall',
      color: 'gray.900',
      pointerEvents: 'none',
      cursor: 'pointer',
      _focus: {
        outline: '2px solid',
        outlineColor: 'blue.500',
        outlineOffset: '2px',
      },
      _disabled: {
        color: 'gray.600',
        cursor: 'default',
      },
    },
    chevronWrapper: {
      alignItems: 'center',
      display: 'flex',
      pointerEvents: 'none',
      color: 'brandColors.primaryDarkLinen',
      sx: {
        '& svg': {
          width: 5,
          height: 5,
          mr: 0,
          color: 'inherit',
          fill: 'currentColor',
        },
        '& svg[aria-disabled="true"]': {
          color: 'inherit',
          fill: 'currentColor',
        },
      },
    },
    chevron: {
      boxSize: 5,
      color: 'brandColors.primaryDarkLinen',
      pointerEvents: 'none',
      _disabled: {
        color: 'gray.400',
      },
    },
    editLink: {
      fontSize: {base: 'sm', md: 'md'},
      fontWeight: 400,
      lineHeight: '1.4',
      color: 'primary.darkLinen',
      height: 'auto',
      minWidth: 'auto',
      gap: 8,
      px: 0,
      py: 0,
      textDecoration: 'underline',
      textUnderlineOffset: '35%',
      sx: {
        textUnderlinePosition: 'from-font',
        '& button': {
          p: 0,
        },
      },
      _hover: {
        color: 'primary.darkLinen',
        textDecoration: 'underline',
      },
      _focus: {
        boxShadow: 'none',
        textDecoration: 'underline',
      },
    },
    content: {
      mt: 4,
    },
  },
  variants: {
    checkout: {
      root: {
        bg: 'transparent',
        border: 'none',
        borderColor: 'transparent',
        borderRadius: 0,
        px: 0,
        py: 6,
        borderBottom: '1px solid',
        borderBottomColor: 'grayAlpha.10',
        shadow: 'none',
        sx: {
          '&.editing': {
            paddingBottom: {'base': '24px', 'lg': '48px'},
          },
          '&:not(:has(+ .toggle-card))': {
            borderBottom: {'base': '1px solid', 'lg': 'none'},
            borderBottomColor: {'base': 'grayAlpha.10', 'lg': 'transparent'},
            marginBottom: {'base': '24px', 'lg': '0'},
            '&.editing': {
              paddingBottom: {'base': '56px', 'lg': '68px'},
            },
          },
          '&.toggle-card-step-0.editing': {
            paddingBottom: {'base': '48px', 'lg': '48px'},
          },
          '&.toggle-card-step-1.editing , &.toggle-card-step-2.editing': {
            paddingBottom: {base: '32px', lg: '48px'},
          },
          '&.toggle-card-step-2.editing, &.toggle-card-step-3.editing': {
            '& .toggle-card-content': {
              paddingTop: {base: '24px', lg: '24px'},
            },
          },
          '&.toggle-card-step-2.editing:has(.chakra-alert)': {
            '& .toggle-card-content': {
              paddingTop: {base: '0', lg: '0'},
            },
            '& .chakra-alert': {
              marginTop: '0',
            },
          },
          '&.toggle-card-step-3.editing': {
            paddingBottom: {base: '56px', lg: '0'},
            '& .checkout-page-place-order': {
              marginBottom: {base: '0', lg: '0'},
              '& span': {
                fontWeight: '600',
              },
            },
            '& .checkout-address-selection-grid': {
              paddingTop: {base: '0', lg: '0'},
            },
            '& .checkout-address-selection-container': {
              '& .checkout-add-new-address-form': {
                paddingTop: '0',
              },
              '& .checkout-add-new-address-heading': {
                paddingTop: '0',
              },
            },
            '& .checkout-add-new-address-heading': {
              marginBottom: '0',
            },
          }
        },
      },
      stack: {
        spacing: 4,
      },
      header: {},
      heading: {
        ...typography.textStyles.labelXL,
        color: 'grayAlpha.90',
        fontFeatureSettings: "'lnum' 1, 'pnum' 1",
        fontStyle: 'normal',
        _disabled: {
          color: 'grayAlpha.90',
          cursor: 'default',
        },
      },
      chevron: {
        boxSize: 5,
        color: 'brandColors.primaryDarkLinen',
      },
      editLink: {
        color: 'brandColors.primaryDarkLinen',
        fontSize: {base: 'sm', md: 'md'},
        fontWeight: 400,
        height: 'auto',
        minWidth: 'auto',
        px: 0,
        py: 0,
        textDecoration: 'underline',
        textUnderlineOffset: '35%',
        sx: {
          textUnderlinePosition: 'from-font',
        },
        _hover: {
          color: 'brandColors.primaryDarkLinen',
          textDecoration: 'underline',
        },
        _focus: {
          boxShadow: 'none',
          textDecoration: 'underline',
        },
      },
      content: {
        mt: 0,
      },
    },
  },
  defaultProps: {
    variant: 'default',
  },
});

export default ToggleCard;
